import React, { useEffect } from 'react';
import { KatButton, KatModal, KatSpinner } from '@amzn/katal-react';
import { useTranslation } from 'src/i18next-arb-shim/useTranslation';
import './SubmitModal.scss';
import {
  ApprovalStatus,
  CompleteSourceReviewInput,
  ReviewType,
  StringGroup,
  useCompleteSourceReviewMutation,
} from 'src/generated/graphql';
import { isStepOneReview } from 'src/views/SourceReview/MessageGroupDetailView/Utils';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  stringGroup: StringGroup;
  userAlias: string | null;
  isBulkEditMode?: boolean;
  onSuccess?: () => void;
};

function getNextApprovalStatus(stringGroup: StringGroup): ApprovalStatus {
  if (
    stringGroup.reviewType === ReviewType.TwoStepReview &&
    stringGroup.approvalStatus === ApprovalStatus.None
  ) {
    return ApprovalStatus.StepOneApproved;
  } else if (
    stringGroup.reviewType === ReviewType.TwoStepReview &&
    stringGroup.approvalStatus === ApprovalStatus.StepOneApproved
  ) {
    return ApprovalStatus.StepTwoApproved;
  } else {
    return ApprovalStatus.Approved;
  }
}

function getLoadingTextResourceId(isStepOneReview: boolean): string {
  if (isStepOneReview) {
    return 'source-review-approve-review-loading';
  } else {
    return 'source-review-submit-for-translation-loading';
  }
}

function getFailedTitleTextResourceId(isStepOneReview: boolean): string {
  if (isStepOneReview) {
    return 'source-review-approve-review-failed-title';
  } else {
    return 'source-review-submit-for-translation-failed-title';
  }
}

function getModalTitleTextResourceId(isStepOneReview: boolean): string {
  if (isStepOneReview) {
    return 'source-review-approve-review-title';
  } else {
    return 'source-review-submit-for-translation-title';
  }
}

function getConfirmationTextResourceId(isStepOneReview: boolean): string {
  if (isStepOneReview) {
    return 'source-review-approve-review-confirmation';
  } else {
    return 'source-review-submit-for-translation-confirmation';
  }
}

function getSubmitButtonTextResourceId(isStepOneReview: boolean): string {
  if (isStepOneReview) {
    return 'source-review-approve-review-button';
  } else {
    return 'source-review-submit-for-translation-button';
  }
}

const SubmitModal: React.FC<Props> = (props: Props) => {
  const { t } = useTranslation();
  const [completeSourceReview, { loading, error, reset }] =
    useCompleteSourceReviewMutation();

  const mutationInput: CompleteSourceReviewInput = {
    stringGroupName: props.stringGroup.stringGroupName,
    userAlias: props.userAlias,
    approvalStatus: getNextApprovalStatus(props.stringGroup),
  };

  // Reset state when the modal opens or closes
  useEffect(() => {
    if (props.isOpen) {
      reset();
    }
  }, [props.isOpen, reset]);

  // GQL query execution upon confirmation of submission for translation
  const submitForTranslation = () => {
    completeSourceReview({
      variables: {
        params: mutationInput,
      },
      errorPolicy: 'all',
    })
      .then((response) => {
        // Submission for translation success
        if (!response.errors) {
          props.onClose();
          if (props.onSuccess) {
            props.onSuccess();
          }
        }
      })
      // Submission for translation error
      .catch((error) => {
        console.error('Error submitting for translation:', error);
      });
  };

  // Modal to show while submission for translation is in progress
  if (loading) {
    return (
      <KatModal
        noCloseIcon={true}
        visible={props.isOpen}
        className="submit-for-translation-loading-modal"
      >
        <div className="submit-for-translation-loading-container">
          <KatSpinner size="small" />
          <div className="submit-for-translation-loading-text">
            {t(getLoadingTextResourceId(isStepOneReview(props.stringGroup)))}
          </div>
        </div>
      </KatModal>
    );
  }

  // Modal to show when error upon submission for translation
  if (error) {
    // Show custom error message for 'string group already submitted for translation'
    // and 'string group in cancelling state'
    let errorMessage;
    if (
      error.message.includes(
        'The current string group review state: CANCELLING'
      )
    ) {
      errorMessage = t(
        'source-review-submit-strings-ongoing-cancellation-error'
      );
    } else if (error.message.includes('has no open source review')) {
      errorMessage = t(
        'source-review-string-group-already-submitted-display-error-message'
      );
    } else {
      errorMessage = t('source-review-server-error-message');
    }

    return (
      <KatModal
        title={t(
          getFailedTitleTextResourceId(isStepOneReview(props.stringGroup))
        )}
        visible={props.isOpen}
        onClose={props.onClose}
        className="submit-for-translation-error-modal"
      >
        <div className="submit-for-translation-error-container">
          <div className="submit-for-translation-error-text">
            {errorMessage}
          </div>
        </div>
        <div slot="footer">
          <KatButton
            id="submit-for-translation-dismiss"
            label={t('source-review-back-to-review-list-button')}
            variant="primary"
            onClick={props.onClose}
          />
        </div>
      </KatModal>
    );
  }

  // Modal to show for confirmation or cancellation of submission for translation
  return (
    <KatModal
      visible={props.isOpen}
      onClose={props.onClose}
      title={t(getModalTitleTextResourceId(isStepOneReview(props.stringGroup)))}
    >
      <div>
        {t(getConfirmationTextResourceId(isStepOneReview(props.stringGroup)))}
      </div>
      <div slot="footer">
        <KatButton
          id="submit-for-translation-cancel"
          label={t('source-review-submit-for-translation-cancel-button')}
          variant="secondary"
          onClick={props.onClose}
          className="submit-for-translation-cancel-button"
        />
        <KatButton
          id="submit-for-translation-submit"
          label={t(
            getSubmitButtonTextResourceId(isStepOneReview(props.stringGroup))
          )}
          variant="primary"
          disabled={props.isBulkEditMode}
          onClick={submitForTranslation}
        />
      </div>
    </KatModal>
  );
};

export default SubmitModal;
